import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

const iso = (d) => d.toISOString().slice(0, 10);
const fmtKm = (n) => (n == null ? '—' : `${Number(n).toFixed(2)} km`);
const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' };

function DeliveryDistanceReport() {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 864e5);
  const [range, setRange] = useState({ startDate: iso(weekAgo), endDate: iso(now) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showPrimary, setShowPrimary] = useState(false);
  const [partners, setPartners] = useState([]);
  const [pDraft, setPDraft] = useState({}); // partnerId -> { lat, lng, label }
  const [savingId, setSavingId] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr(''); setMsg('');
    try {
      const res = await adminAPI.getDeliveryDistanceReport(range);
      setData(res.data.data);
    } catch (e) { setErr(e.response?.data?.message || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [range]);

  const loadPartners = useCallback(async () => {
    try {
      const res = await adminAPI.getDeliveryPartnerPrimaryLocations();
      const list = res.data.data || [];
      setPartners(list);
      const draft = {};
      list.forEach((p) => {
        draft[p.partnerId] = {
          lat: p.primaryLocation?.lat ?? '',
          lng: p.primaryLocation?.lng ?? '',
          label: p.primaryLocation?.label ?? '',
        };
      });
      setPDraft(draft);
    } catch (e) { setErr(e.response?.data?.message || 'Failed to load partners'); }
  }, []);

  const savePrimary = async (partnerId) => {
    const d = pDraft[partnerId] || {};
    const lat = Number(d.lat), lng = Number(d.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) { setErr('Enter valid latitude & longitude'); return; }
    setSavingId(partnerId); setErr(''); setMsg('');
    try {
      await adminAPI.updateDeliveryPartnerPrimaryLocation(partnerId, { lat, lng, label: d.label || '' });
      setMsg('Primary location saved');
      await loadPartners();
    } catch (e) { setErr(e.response?.data?.message || 'Failed to save primary location'); }
    finally { setSavingId(''); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);
  useEffect(() => { if (showPrimary && partners.length === 0) loadPartners(); /* eslint-disable-next-line */ }, [showPrimary]);

  const backfill = async () => {
    if (!window.confirm('Compute route distances for delivered orders in this range that are missing them? (uses Google Directions, cached)')) return;
    setBackfilling(true); setErr(''); setMsg('');
    try {
      const res = await adminAPI.backfillDeliveryDistance(range);
      setMsg(res.data.message || 'Backfill complete');
      await load();
    } catch (e) { setErr(e.response?.data?.message || 'Backfill failed'); }
    finally { setBackfilling(false); }
  };

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 20 }}>
        <h1 style={{ marginBottom: 4 }}>🛵 Delivery Distance (actual route)</h1>
        <p style={{ color: '#64748B', marginTop: 0 }}>
          Per delivery partner → per day → per order: actual road distance for accept→restaurant (pickup) + restaurant→user (drop) + return-to-base (user→home base, only when the partner got no new order within 20 min of delivering).
        </p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', margin: '12px 0' }}>
          <label>From<br /><input type="date" value={range.startDate} onChange={(e) => setRange({ ...range, startDate: e.target.value })} /></label>
          <label>To<br /><input type="date" value={range.endDate} onChange={(e) => setRange({ ...range, endDate: e.target.value })} /></label>
          <button onClick={load} disabled={loading} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#FF6961', color: '#fff', cursor: 'pointer' }}>{loading ? 'Loading…' : 'Refresh'}</button>
          <button onClick={backfill} disabled={backfilling} title="Compute distances for orders missing them" style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>{backfilling ? 'Computing…' : '⟳ Backfill missing'}</button>
          <button onClick={() => setShowPrimary((v) => !v)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: showPrimary ? '#EEF2FF' : '#fff', cursor: 'pointer' }}>📍 Primary locations</button>
        </div>

        {showPrimary && (
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>📍 Partner primary (home-base) locations</h3>
              <button onClick={loadPartners} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>Reload</button>
            </div>
            <p style={{ color: '#64748B', fontSize: 12, marginTop: 0 }}>
              Set each approved partner's home base. After a delivery, if they take no new order within 20 min, the route from the customer back to this base is added to that order's distance.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead><tr style={{ color: '#64748B', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>Partner</th>
                  <th style={{ padding: '6px 8px' }}>Latitude</th>
                  <th style={{ padding: '6px 8px' }}>Longitude</th>
                  <th style={{ padding: '6px 8px' }}>Label</th>
                  <th style={{ padding: '6px 8px' }}></th>
                </tr></thead>
                <tbody>
                  {partners.map((p) => {
                    const d = pDraft[p.partnerId] || {};
                    const set = (k, v) => setPDraft((x) => ({ ...x, [p.partnerId]: { ...x[p.partnerId], [k]: v } }));
                    const hasSaved = p.primaryLocation && Number.isFinite(p.primaryLocation.lat);
                    return (
                      <tr key={p.partnerId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '6px 8px' }}>{p.name}<div style={{ color: '#94a3b8', fontSize: 11 }}>{p.phone}{hasSaved ? '' : ' · not set'}</div></td>
                        <td style={{ padding: '6px 8px' }}><input value={d.lat ?? ''} onChange={(e) => set('lat', e.target.value)} placeholder="lat" style={{ width: 110 }} /></td>
                        <td style={{ padding: '6px 8px' }}><input value={d.lng ?? ''} onChange={(e) => set('lng', e.target.value)} placeholder="lng" style={{ width: 110 }} /></td>
                        <td style={{ padding: '6px 8px' }}><input value={d.label ?? ''} onChange={(e) => set('label', e.target.value)} placeholder="e.g. Nehru Nagar hub" style={{ width: 160 }} /></td>
                        <td style={{ padding: '6px 8px' }}><button onClick={() => savePrimary(p.partnerId)} disabled={savingId === p.partnerId} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: '#FF6961', color: '#fff', cursor: 'pointer' }}>{savingId === p.partnerId ? 'Saving…' : 'Save'}</button></td>
                      </tr>
                    );
                  })}
                  {partners.length === 0 && <tr><td colSpan={5} style={{ padding: 12, color: '#999' }}>No approved delivery partners.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {err && <div style={{ ...card, borderColor: '#EF4444', color: '#DC2626', marginBottom: 12 }}>{err}</div>}
        {msg && <div style={{ ...card, borderColor: '#10B981', color: '#059669', marginBottom: 12 }}>{msg}</div>}
        {loading && <div style={{ padding: 16 }}>🔄 Loading…</div>}

        {data && !loading && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 12, marginBottom: 16 }}>
              <div style={card}><div style={{ color: '#777', fontSize: 12 }}>Total route distance</div><div style={{ fontSize: 22, fontWeight: 700 }}>{fmtKm(data.grandTotalKm)}</div></div>
              <div style={card}><div style={{ color: '#777', fontSize: 12 }}>Partners</div><div style={{ fontSize: 22, fontWeight: 700 }}>{data.partners.length}</div></div>
              <div style={card}><div style={{ color: '#777', fontSize: 12 }}>Orders (computed)</div><div style={{ fontSize: 22, fontWeight: 700 }}>{data.computedOrders}</div></div>
              <div style={card}><div style={{ color: '#777', fontSize: 12 }}>Not yet computed</div><div style={{ fontSize: 22, fontWeight: 700, color: data.uncomputedOrders ? '#D97706' : '#111' }}>{data.uncomputedOrders}</div></div>
            </div>
            {data.uncomputedOrders > 0 && (
              <p style={{ fontSize: 12, color: '#B45309', marginTop: -6 }}>
                ⚠ {data.uncomputedOrders} delivered orders have no route distance yet — click <b>Backfill missing</b>. The drop leg (restaurant→user) computes for all; the pickup leg (accept→restaurant) only where the rider's accept-location was captured (orders after this feature launched).
              </p>
            )}

            {data.partners.length === 0 && <div style={{ ...card, color: '#999' }}>No delivered orders by any partner in this range.</div>}

            {data.partners.map((p) => (
              <div key={p.partnerId} style={{ ...card, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', flexWrap: 'wrap', gap: 8 }} onClick={() => setExpanded((x) => ({ ...x, [p.partnerId]: !x[p.partnerId] }))}>
                  <div>
                    <span style={{ fontWeight: 700 }}>{expanded[p.partnerId] ? '▾' : '▸'} {p.name}</span>
                    <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 13 }}>{p.phone}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 13, flexWrap: 'wrap' }}>
                    <span>{p.orderCount} orders</span>
                    <span>pickup <b>{fmtKm(p.pickupKm)}</b></span>
                    <span>drop <b>{fmtKm(p.dropKm)}</b></span>
                    <span style={{ color: '#7C3AED' }}>return <b>{fmtKm(p.returnKm)}</b></span>
                    <span style={{ color: '#059669' }}>total <b>{fmtKm(p.totalKm)}</b></span>
                  </div>
                </div>
                {expanded[p.partnerId] && (
                  <div style={{ marginTop: 12 }}>
                    {p.days.map((d) => (
                      <div key={d.date} style={{ marginBottom: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, borderBottom: '1px solid #eee', padding: '4px 0' }}>{d.date} · {d.orderCount} orders · total {fmtKm(d.totalKm)}</div>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                            <thead><tr style={{ color: '#64748B', textAlign: 'right' }}>
                              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Order</th>
                              <th style={{ padding: '4px 6px' }}>Delivered</th>
                              <th style={{ padding: '4px 6px' }}>Accept→Restaurant</th>
                              <th style={{ padding: '4px 6px' }}>Restaurant→User</th>
                              <th style={{ padding: '4px 6px' }}>Return→Base</th>
                              <th style={{ padding: '4px 6px' }}>Total</th>
                            </tr></thead>
                            <tbody>
                              {d.orders.map((o) => (
                                <tr key={o.orderNumber} style={{ textAlign: 'right', borderBottom: '1px solid #f3f4f6' }}>
                                  <td style={{ textAlign: 'left', padding: '4px 6px' }}>#{o.orderNumber}</td>
                                  <td style={{ padding: '4px 6px', color: '#94a3b8' }}>{new Date(o.deliveredAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                                  <td style={{ padding: '4px 6px' }}>{fmtKm(o.acceptToRestaurantKm)}</td>
                                  <td style={{ padding: '4px 6px' }}>{fmtKm(o.restaurantToUserKm)}</td>
                                  {(() => {
                                    let label, color, tip, small = false;
                                    if (o.returnToBaseKm != null) { label = fmtKm(o.returnToBaseKm); color = '#7C3AED'; tip = 'Idle >20 min after delivery — returned to base'; }
                                    else if (!o.returnEvaluated) { label = '⏳'; color = '#cbd5e1'; tip = 'Idle window (20 min) not evaluated yet'; }
                                    else if (o.returnIdle === false) { label = '—'; color = '#94a3b8'; tip = 'Took a new order within 20 min — no return leg'; }
                                    else if (o.returnIdle === true) { label = 'set base'; color = '#D97706'; small = true; tip = 'Idle >20 min, but this partner has no primary location set. Set it, then Backfill.'; }
                                    else { label = 'not tracked'; color = '#cbd5e1'; small = true; tip = 'Accept time not recorded for this order (delivered before rider-tracking launched) — the 20-min rule cannot be applied, so no return leg.'; }
                                    return <td style={{ padding: '4px 6px', color, fontSize: small ? 10 : 12 }} title={tip}>{label}</td>;
                                  })()}
                                  <td style={{ padding: '4px 6px', fontWeight: 600 }}>{fmtKm(o.totalKm)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default DeliveryDistanceReport;
